import { DynamicObjectInterface } from '../../sdk/type';
import { Language } from '../../ui/components';
import { LoggerService } from '../../ui/utils';
import { REACT_CACHE_TIME } from '../../envVars';
import { ContentStackService, ContentStackConsentService, ContentStackArticleService } from './contentStackService';

export const fetchContent = async (contentType: string): Promise<DynamicObjectInterface | null> => {
  try {
    const result = await ContentStackService.getContentStackDataConfig(Language.English);
    return result.data && result.data.length > 0 ? result.data[0][contentType] : null;
  } catch (error) {
    LoggerService.log('Failed to fetch content:', error);
    return null;
  }
};

export const fetchConsentContent = async (): Promise<DynamicObjectInterface | null> => {
  try {
    const result = await ContentStackConsentService.getContentStackDataConsent(Language.English);
    return result.data && result.data.length > 0 ? result.data[0] : null;
  } catch (error) {
    LoggerService.log('Failed to fetch content:', error);
    return null;
  }
};

export const cacheContent = (key: string, data: DynamicObjectInterface) => {
  localStorage.setItem(key, JSON.stringify(data));
  localStorage.setItem(`${key}Timestamp`, new Date().getTime().toString());
};

export const getCachedContent = (key: string): DynamicObjectInterface | null => {
  const cachedContent = localStorage.getItem(key);
  if (cachedContent) {
    return JSON.parse(cachedContent);
  }
  return null;
};

export const isContentCacheValid = (key: string, cacheExpirationTime: number): boolean => {
  const cachedContentTimestamp = localStorage.getItem(`${key}Timestamp`);

  if (cachedContentTimestamp !== null) {
    return new Date().getTime() - parseInt(cachedContentTimestamp) < cacheExpirationTime;
  }

  return false;
};

export const getContent = async (
  contentType: string,
  cacheKey: string,
  useCache: boolean,
): Promise<DynamicObjectInterface> => {
  let content: DynamicObjectInterface | null = null;
  let cacheExpirationTime = REACT_CACHE_TIME ? parseInt(REACT_CACHE_TIME) : 60 * 60 * 1000;
  if (useCache) {
    content = getCachedContent(cacheKey);

    if (!isContentCacheValid(cacheKey, cacheExpirationTime)) {
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}Timestamp`);
      content = await fetchContent(contentType);
      if (content) {
        cacheContent(cacheKey, content);
      }
    }
  } else {
    content = await fetchContent(contentType);
  }
  return content;
};

export const getConsent = async (cacheKey: string, useCache: boolean): Promise<DynamicObjectInterface> => {
  let content: DynamicObjectInterface | null = null;
  let cacheExpirationTime = REACT_CACHE_TIME ? parseInt(REACT_CACHE_TIME) : 60 * 60 * 1000;
  if (useCache) {
    content = getCachedContent(cacheKey);
    if (!isContentCacheValid(cacheKey, cacheExpirationTime)) {
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}Timestamp`);
      content = await fetchConsentContent();
      if (content) {
        cacheContent(cacheKey, content);
      }
    }
  } else {
    content = await fetchConsentContent();
  }

  return content || {};
};

export const getLatestArticle = async (
  cacheKey: string,
  useCache: boolean,
  numberOfItem: number,
  categoryId?: string,
  subCategoryId?: string,
): Promise<DynamicObjectInterface> => {
  let content: DynamicObjectInterface | null = null;
  let cacheExpirationTime = REACT_CACHE_TIME ? parseInt(REACT_CACHE_TIME) : 60 * 60 * 1000;
  console.log('REACT_CACHE_TIME:', REACT_CACHE_TIME);

  if (useCache) {
    content = getCachedContent(cacheKey);

    if (!isContentCacheValid(cacheKey, cacheExpirationTime)) {
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(`${cacheKey}Timestamp`);
      content = await fetchLatestCategory(numberOfItem, categoryId, subCategoryId);
      if (content) {
        cacheContent(cacheKey, content);
      }
    }
  } else {
    content = await fetchLatestCategory(numberOfItem, categoryId, subCategoryId);
  }

  return content || {};
};

export const fetchLatestCategory = async (
  numberOfItem: number,
  categoryId?: string,
  subCategoryId?: string,
): Promise<DynamicObjectInterface | null> => {
  try {
    const result = await ContentStackArticleService.getArticleByCategoryId(
      Language.English,
      numberOfItem,
      categoryId,
      subCategoryId,
    );
    return result.data && result.data.length > 0 ? result.data : null;
  } catch (error) {
    LoggerService.log('Failed to fetch content:', error);
    return null;
  }
};
